import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Link as LinkIcon,
  Link2Off,
  List as ListIcon,
  ListOrdered,
  Quote,
  Code,
  Heading2,
  Heading3,
  Palette,
} from "lucide-react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import type { RangeSelection } from "lexical";
import { $createParagraphNode, $createTextNode, $getRoot, $getSelection, $isRangeSelection, $setSelection } from "lexical";
import { COMMAND_PRIORITY_LOW, SELECTION_CHANGE_COMMAND } from "lexical";
import { HeadingNode, QuoteNode, $createHeadingNode, $createQuoteNode } from "@lexical/rich-text";
import { CodeNode } from "@lexical/code";
import { $createLinkNode, $isLinkNode, $toggleLink, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode, insertList, removeList } from "@lexical/list";
import { $patchStyleText, $setBlocksType } from "@lexical/selection";

type Props = {
  value: string;
  onChange: (nextHtml: string) => void;
  className?: string;
  namespace: string;
  placeholder?: string;
  minHeightClassName?: string;
};

const LINK_UI_EVENT = "lexical-html-editor-link-ui";

function Toolbar({ namespace }: { namespace: string }) {
  const [editor] = useLexicalComposerContext();
  const lastSelectionRef = useRef<RangeSelection | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [activeLinkUrl, setActiveLinkUrl] = useState<string | null>(null);
  const [textColor, setTextColor] = useState("#000000");

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        editor.getEditorState().read(() => {
          const sel = $getSelection();
          if ($isRangeSelection(sel)) {
            lastSelectionRef.current = sel.clone();
          }

          const selectionForLink = $isRangeSelection(sel) ? sel : lastSelectionRef.current;
          let nextUrl: string | null = null;
          if (selectionForLink && $isRangeSelection(selectionForLink)) {
            const nodes = selectionForLink.getNodes();
            for (const node of nodes) {
              let cur = node;
              while (cur) {
                if ($isLinkNode(cur)) {
                  nextUrl = cur.getURL();
                  break;
                }
                cur = cur.getParent();
              }
              if (nextUrl) break;
            }
          }

          setActiveLinkUrl((prev) => (prev === nextUrl ? prev : nextUrl));
        });
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor]);

  const restoreSelectionIfNeeded = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        lastSelectionRef.current = selection.clone();
        return;
      }
      if (lastSelectionRef.current) {
        $setSelection(lastSelectionRef.current);
      }
    });
  };

  const run = (fn: () => void) => {
    editor.focus();
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        lastSelectionRef.current = selection.clone();
      } else if (lastSelectionRef.current) {
        $setSelection(lastSelectionRef.current);
      }
      fn();
    });
  };

  useEffect(() => {
    const eventName = `${LINK_UI_EVENT}:${namespace}`;
    const onLinkUi = (e: Event) => {
      const ce = e as CustomEvent<{ url?: string; action?: "open" | "remove" }>;
      const url = (ce.detail?.url ?? "").trim();
      const action = ce.detail?.action ?? "open";
      if (action === "remove") {
        run(removeLink);
        return;
      }
      setLinkUrl(url);
      setLinkOpen(true);
    };
    window.addEventListener(eventName, onLinkUi);
    return () => window.removeEventListener(eventName, onLinkUi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, namespace]);

  const onToolbarMouseDown = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    run(fn);
  };

  const setHeading = (level: 2 | 3) => () => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
    const tag = level === 2 ? "h2" : "h3";
    $setBlocksType(selection, () => $createHeadingNode(tag));
  };

  const setQuote = () => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
    $setBlocksType(selection, () => $createQuoteNode());
  };

  const setParagraph = () => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
    $setBlocksType(selection, () => $createParagraphNode());
  };

  const openLink = () => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes();
        for (const node of nodes) {
          if ($isLinkNode(node)) {
            setLinkUrl(node.getURL());
            setLinkOpen(true);
            return;
          }
          const parent = node.getParent();
          if (parent && $isLinkNode(parent)) {
            setLinkUrl(parent.getURL());
            setLinkOpen(true);
            return;
          }
        }
      }
      setLinkUrl("");
      setLinkOpen((v) => !v);
    });
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    editor.focus();
    editor.update(() => {
      const stored = lastSelectionRef.current;
      if (stored) $setSelection(stored);
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (!url) {
        $toggleLink(null);
        return;
      }

      if (selection.isCollapsed()) {
        const linkNode = $createLinkNode(url);
        linkNode.append($createTextNode(url));
        selection.insertNodes([linkNode]);
        return;
      }

      $toggleLink(url);
    });
    setLinkOpen(false);
    setLinkUrl("");
  };

  const removeLink = () => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
    $toggleLink(null);
    setLinkOpen(false);
    setLinkUrl("");
  };

  const applyTextColor = (next: string) => {
    setTextColor(next);
    run(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      $patchStyleText(selection, { color: next });
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-1 border-b bg-white px-2 py-1">
      <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onMouseDown={onToolbarMouseDown(setHeading(2))} title="Heading 2">
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onMouseDown={onToolbarMouseDown(setHeading(3))} title="Heading 3">
        <Heading3 className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onMouseDown={onToolbarMouseDown(setParagraph)} title="Paragraph">
        <span className="text-xs font-semibold">P</span>
      </Button>

      <div className="mx-1 h-5 w-px bg-gray-200" />

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2"
        onMouseDown={onToolbarMouseDown(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) selection.formatText("bold");
        })}
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2"
        onMouseDown={onToolbarMouseDown(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) selection.formatText("italic");
        })}
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2"
        onMouseDown={onToolbarMouseDown(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) selection.formatText("underline");
        })}
        title="Underline"
      >
        <UnderlineIcon className="h-4 w-4" />
      </Button>

      <div className="mx-1 h-5 w-px bg-gray-200" />

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2"
        onMouseDown={onToolbarMouseDown(() => insertList(editor, "bullet"))}
        title="Bulleted list"
      >
        <ListIcon className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2"
        onMouseDown={onToolbarMouseDown(() => insertList(editor, "number"))}
        title="Numbered list"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2"
        onMouseDown={onToolbarMouseDown(() => removeList(editor))}
        title="Remove list"
      >
        <span className="text-xs font-semibold">×</span>
      </Button>
      <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onMouseDown={onToolbarMouseDown(setQuote)} title="Quote">
        <Quote className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onMouseDown={onToolbarMouseDown(openLink)} title="Link">
        <LinkIcon className="h-4 w-4" />
      </Button>
      {activeLinkUrl ? (
        <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onMouseDown={onToolbarMouseDown(removeLink)} title={`Remove link (${activeLinkUrl})`}>
          <Link2Off className="h-4 w-4" />
        </Button>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2"
        onMouseDown={onToolbarMouseDown(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) selection.formatText("code");
        })}
        title="Inline code"
      >
        <Code className="h-4 w-4" />
      </Button>

      <div className="mx-1 h-5 w-px bg-gray-200" />

      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <Palette className="h-4 w-4 text-gray-700" />
          <input
            type="color"
            value={textColor}
            onChange={(e) => applyTextColor(e.target.value)}
            className="h-7 w-8 cursor-pointer rounded border border-gray-200 bg-white p-0"
            title="Text color"
          />
        </div>
      </div>

      {linkOpen ? (
        <div className="ml-2 flex items-center gap-2">
          <input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://…"
            className="h-8 w-[240px] rounded-md border border-gray-200 px-2 text-xs outline-none focus:ring-2 focus:ring-primary/40"
          />
          <Button type="button" size="sm" className="h-8" onMouseDown={(e) => { e.preventDefault(); applyLink(); }}>
            Apply
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8" onMouseDown={(e) => { e.preventDefault(); run(removeLink); }}>
            Remove
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8" onMouseDown={(e) => { e.preventDefault(); setLinkOpen(false); setLinkUrl(""); }}>
            Cancel
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function HtmlSyncPlugin({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const [editor] = useLexicalComposerContext();
  const lastLoadedRef = useRef<string | null>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    // Only load when parent-provided value changes.
    if (value === (lastLoadedRef.current ?? "")) return;
    lastLoadedRef.current = value;

    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const html = value?.trim() ?? "";
      if (!html) {
        root.append($createParagraphNode());
        return;
      }
      const dom = new DOMParser().parseFromString(html, "text/html");
      const nodes = $generateNodesFromDOM(editor, dom);
      root.append(...nodes);
    });
  }, [editor, value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    };
  }, []);

  return (
    <OnChangePlugin
      onChange={(editorState) => {
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => {
          editorState.read(() => {
            const html = $generateHtmlFromNodes(editor);
            lastLoadedRef.current = html;
            onChange(html);
          });
        }, 200);
      }}
    />
  );
}

export default function LexicalHtmlEditor({
  value,
  onChange,
  className,
  namespace,
  placeholder = "Write…",
  minHeightClassName = "min-h-[280px]",
}: Props) {
  const initialConfig = useMemo(
    () => ({
      namespace,
      onError: (error: Error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      },
      theme: {
        text: {
          bold: "font-semibold",
          italic: "italic",
          underline: "underline",
          code: "rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.9em]",
        },
        link: "text-primary underline underline-offset-2",
        quote: "border-l-4 border-gray-200 pl-4 italic text-gray-700",
      },
      nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, CodeNode, LinkNode],
    }),
    [namespace]
  );

  const eventName = `${LINK_UI_EVENT}:${namespace}`;

  return (
    <div className={className}>
      <div className="rounded-md border border-gray-200 bg-white">
        <LexicalComposer initialConfig={initialConfig}>
          <Toolbar namespace={namespace} />
          <div className="p-0">
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={cn(
                    minHeightClassName,
                    "w-full px-3 py-3 text-sm leading-6 outline-none",
                    "prose prose-sm max-w-none",
                    "prose-headings:font-poppins prose-headings:font-semibold",
                    "prose-h2:text-2xl prose-h3:text-xl",
                    "prose-ul:list-disc prose-ol:list-decimal prose-li:my-1 prose-ul:pl-6 prose-ol:pl-6",
                    "focus-visible:ring-2 focus-visible:ring-primary/40",
                    "[&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5"
                  )}
                  onMouseOver={(e) => {
                    const target = e.target as HTMLElement | null;
                    const a = target?.closest?.("a") as HTMLAnchorElement | null;
                    if (!a) return;
                    if (!a.title) a.title = a.href;
                  }}
                  onClick={(e) => {
                    const target = e.target as HTMLElement | null;
                    const a = target?.closest?.("a") as HTMLAnchorElement | null;
                    if (!a) return;

                    if (e.metaKey || e.ctrlKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(a.href, "_blank", "noopener,noreferrer");
                      return;
                    }

                    e.preventDefault();
                    const url = (a.getAttribute("href") ?? a.href) || "";
                    window.setTimeout(() => {
                      window.dispatchEvent(new CustomEvent(eventName, { detail: { action: "open", url } }));
                    }, 0);
                  }}
                />
              }
              placeholder={<div className="pointer-events-none px-3 py-3 text-sm text-gray-400">{placeholder}</div>}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            <ListPlugin />
            <LinkPlugin />
            <HtmlSyncPlugin value={value} onChange={onChange} />
          </div>
        </LexicalComposer>
      </div>
    </div>
  );
}


